import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { ConfirmModal } from '../../components/Common/ConfirmModal';
import { useToast } from '../../components/Common/Toast';
import {
    BilanCalendrier, BilanCalendrierModal, Completude, CompletudeModal,
    ExtractionAvis, ExtractionAvisModal, ExtractionMeta, fcfa,
} from './AvisDividendesModals';
import './AdminDiscoveredAvisScreen.css';

/** Message d'erreur lisible depuis une réponse d'API (jamais le texte brut). */
const messageErreur = (data: unknown, defaut: string): string => {
    const detail = (data as { detail?: unknown } | null)?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
        return String((detail as { message: unknown }).message);
    }
    return defaut;
};

interface ProcessResult {
    status?: string;
    message?: string;
    details?: { action?: string; message?: string; bilan?: BilanCalendrier };
}

/** Résumé en français d'un traitement, à la place du JSON brut. */
const resumeTraitement = (res: ProcessResult): string => {
    const action = res.details?.action;
    const bilan = res.details?.bilan;
    if (bilan) {
        const c = bilan.compteurs;
        return `Calendrier : ${bilan.lignes_lues} lignes lues, ${(c.DEJA_EN_BASE || 0) + (c.COMPLEMENT || 0)} déjà en base, `
            + `${c.INSERTION || 0} insérées${bilan.a_revoir ? ' — revue requise (voir le bilan)' : ''}.`;
    }
    if (action === 'UPSERT_CORPORATE_ACTION') return 'Opération sur titres enregistrée.';
    if (action === 'IMPORT_DIVIDENDE_AVIS') return 'Dividende importé.';
    if (action === 'DEJA_EN_BASE') return 'Dividende déjà en base : avis traité.';
    return res.message || res.details?.message || 'Traitement effectué.';
};

interface DiscoveredAvis {
    id: number;
    avis_url: string;
    source_url: string;
    title: string;
    societe_symbole?: string | null;
    category: string;
    date_publication?: string | null;
    date_effective?: string | null;
    statut: string;
    caution_flag: boolean;
    caution_reason?: string | null;
    extracted_data_json?: string | null;
    processed_at?: string | null;
    discovered_at?: string | null;
    exercice?: number | null;
    dividende_en_base?: boolean | null;
}

type ConfirmationAction = { type: 'process' | 'dismiss'; avis: DiscoveredAvis };

interface Stock {
    symbole: string;
    nom_societe?: string;
}

const CATEGORY_CONFIG: Record<string, { className: string; label: string; icon: string }> = {
    CALENDRIER_DIVIDENDES: { className: 'cat-dividendes', label: 'Calendrier Dividendes', icon: 'event_note' },
    FRACTIONNEMENT: { className: 'cat-fractionnement', label: 'Fractionnement', icon: 'call_split' },
    AUGMENTATION_CAPITAL: { className: 'cat-augmentation', label: 'Augmentation Capital', icon: 'trending_up' },
    PAIEMENT_DIVIDENDE: { className: 'cat-paiement', label: 'Paiement Dividende', icon: 'payments' },
    COMMUNIQUE: { className: 'cat-communique', label: 'Communiqué', icon: 'campaign' },
    AUTRE: { className: 'cat-autre', label: 'Autre', icon: 'description' },
};

const STATUT_CONFIG: Record<string, { className: string; label: string }> = {
    NOUVEAU: { className: 'status-nouveau', label: 'Nouveau' },
    TRAITE: { className: 'status-traite', label: 'Traité' },
    EN_ATTENTE_REVUE: { className: 'status-en_attente', label: 'En attente revue' },
    IGNORE: { className: 'status-ignore', label: 'Ignoré' },
    ERREUR: { className: 'status-erreur', label: 'Erreur' },
};

const CategoryBadge: React.FC<{ category: string }> = ({ category }) => {
    const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.AUTRE;
    return (
        <span className={`category-badge ${config.className}`}>
            <i className="material-icons" style={{ fontSize: '13px' }}>{config.icon}</i>
            {config.label}
        </span>
    );
};

const StatutBadge: React.FC<{ statut: string }> = ({ statut }) => {
    const config = STATUT_CONFIG[statut] || STATUT_CONFIG.NOUVEAU;
    return (
        <span className={`status-pill ${config.className}`}>
            <span className="status-dot" />
            {config.label}
        </span>
    );
};

const AdminDiscoveredAvisScreen: React.FC = () => {
    const { token } = useAuth();
    const { showToast } = useToast();
    const [avisList, setAvisList] = useState<DiscoveredAvis[]>([]);
    const [stocks, setStocks] = useState<Stock[]>([]);
    const [loading, setLoading] = useState(false);

    // Filters
    const [statutFilter, setStatutFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [societeFilter, setSocieteFilter] = useState('');
    const [cautionOnly, setCautionOnly] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [enBaseFilter, setEnBaseFilter] = useState('');

    // Traitement des dividendes : bilan de calendrier, proposition d'import, complétude
    const [bilanCalendrier, setBilanCalendrier] = useState<{ avis: DiscoveredAvis; bilan: BilanCalendrier } | null>(null);
    const [extraction, setExtraction] = useState<{ avis: DiscoveredAvis; data: ExtractionAvis; meta: ExtractionMeta } | null>(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [confirmation, setConfirmation] = useState<ConfirmationAction | null>(null);
    const [completudeOuverte, setCompletudeOuverte] = useState(false);
    const [completudeExercice, setCompletudeExercice] = useState(new Date().getFullYear() - 1);
    const [completude, setCompletude] = useState<Completude | null>(null);
    const [completudeLoading, setCompletudeLoading] = useState(false);

    // Scan
    const [scanLoading, setScanLoading] = useState(false);
    const [scanPages, setScanPages] = useState(5);
    const [scanMessage, setScanMessage] = useState<{ text: string; isError?: boolean } | null>(null);

    // Inline edit
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingSymbole, setEditingSymbole] = useState('');
    const [editingCategory, setEditingCategory] = useState('');

    // Process
    const [processingId, setProcessingId] = useState<number | null>(null);
    const [processResults, setProcessResults] = useState<Record<number, ProcessResult>>({});
    const [processErrors, setProcessErrors] = useState<Record<number, string>>({});

    // Caution modal for SAFCA
    const [cautionModalAvis, setCautionModalAvis] = useState<DiscoveredAvis | null>(null);

    const fetchStocks = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/v1/stocks/active`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setStocks(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching stocks:', error);
        }
    }, [token]);

    const fetchAvis = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (statutFilter) params.set('statut', statutFilter);
            if (categoryFilter) params.set('category', categoryFilter);
            if (societeFilter) params.set('societe_symbole', societeFilter);
            if (cautionOnly) params.set('caution_only', 'true');
            if (dateFrom) params.set('date_from', dateFrom);
            if (dateTo) params.set('date_to', dateTo);
            if (enBaseFilter) params.set('en_base', enBaseFilter);

            const qs = params.toString() ? `?${params.toString()}` : '';
            const response = await fetch(`${API_URL}/v1/admin/discovered-avis${qs}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setAvisList(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching avis:', error);
        } finally {
            setLoading(false);
        }
    }, [token, statutFilter, categoryFilter, societeFilter, cautionOnly, dateFrom, dateTo, enBaseFilter]);

    useEffect(() => {
        fetchStocks();
    }, [fetchStocks]);

    useEffect(() => {
        fetchAvis();
    }, [fetchAvis]);

    // KPI counts
    const kpis = useMemo(() => {
        const total = avisList.length;
        const nouveaux = avisList.filter(a => a.statut === 'NOUVEAU').length;
        const traites = avisList.filter(a => a.statut === 'TRAITE').length;
        const cautions = avisList.filter(a => a.caution_flag || a.statut === 'EN_ATTENTE_REVUE').length;
        const ignores = avisList.filter(a => a.statut === 'IGNORE').length;
        return { total, nouveaux, traites, cautions, ignores };
    }, [avisList]);

    const hasActiveFilters = Boolean(statutFilter || categoryFilter || societeFilter || cautionOnly || dateFrom || dateTo || enBaseFilter);

    const handleResetFilters = () => {
        setStatutFilter('');
        setCategoryFilter('');
        setSocieteFilter('');
        setCautionOnly(false);
        setDateFrom('');
        setDateTo('');
        setEnBaseFilter('');
    };

    const fetchCompletude = useCallback(async (exercice: number) => {
        if (!token) return;
        setCompletudeLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/discovered-avis/completude?exercice=${exercice}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            if (response.ok) {
                setCompletude(data);
            } else {
                showToast(messageErreur(data, 'Contrôle de complétude indisponible.'), 'error');
            }
        } catch {
            showToast('Erreur réseau : contrôle de complétude indisponible.', 'error');
        } finally {
            setCompletudeLoading(false);
        }
    }, [token, showToast]);

    useEffect(() => {
        if (completudeOuverte) fetchCompletude(completudeExercice);
    }, [completudeOuverte, completudeExercice, fetchCompletude]);

    const handleTriggerScan = async () => {
        if (!token) return;
        setScanLoading(true);
        setScanMessage(null);
        // brvm.org impose 10 s entre deux pages : au-delà de 5 pages (+ 3 pages ESV), la
        // requête dépasserait la limite de 230 s d'App Service. Le scan part alors en
        // arrière-plan et l'écran se recharge à la demande.
        const enArrierePlan = scanPages > 5;
        try {
            const response = await fetch(`${API_URL}/v1/admin/discovered-avis/scan?max_pages=${scanPages}&run_in_background=${enArrierePlan}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok && enArrierePlan) {
                setScanMessage({
                    text: `Scan de ${scanPages} pages lancé en arrière-plan (environ ${Math.ceil((scanPages + 3) * 11 / 60)} min). Rechargez la liste ensuite.`
                });
            } else if (response.ok) {
                const data = await response.json();
                const res = data.result || {};
                const esv = res.esv_paiements || {};
                setScanMessage({
                    text: `Scan BRVM terminé : ${res.new_discovered ?? 0} nouveaux avis sur ${res.scanned ?? 0} examinés, `
                        + `${esv.nouveaux ?? 0} nouveaux avis de paiement de dividendes (page ESV), `
                        + `${res.reclassified ?? 0} avis reclassés, ${(res.updated ?? 0) + (esv.completes ?? 0)} rattachements corrigés.`
                });
                fetchAvis();
            } else {
                setScanMessage({ text: 'Erreur lors du scan des avis BRVM.', isError: true });
            }
        } catch (error) {
            setScanMessage({ text: `Erreur réseau : ${error}`, isError: true });
        } finally {
            setScanLoading(false);
        }
    };

    const handleStartEdit = (avis: DiscoveredAvis) => {
        setEditingId(avis.id);
        setEditingSymbole(avis.societe_symbole || '');
        setEditingCategory(avis.category);
    };

    const handleSaveEdit = async (avisId: number) => {
        if (!token) return;
        try {
            const response = await fetch(`${API_URL}/v1/admin/discovered-avis/${avisId}`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    societe_symbole: editingSymbole || null,
                    category: editingCategory,
                }),
            });
            if (response.ok) {
                setEditingId(null);
                fetchAvis();
            }
        } catch (error) {
            console.error('Error saving avis edit:', error);
        }
    };

    /** Appel de /process ; `dry_run` ne concerne que les calendriers. */
    const appelProcess = async (avis: DiscoveredAvis, opts: { force?: boolean; dry_run?: boolean }) => {
        const response = await fetch(`${API_URL}/v1/admin/discovered-avis/${avis.id}/process`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ force: Boolean(opts.force), dry_run: Boolean(opts.dry_run) }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(messageErreur(data, 'Erreur de traitement.'));
        return data as ProcessResult;
    };

    /**
     * « Traiter » selon la catégorie :
     * - calendrier : SIMULATION d'abord, le bilan s'affiche, l'écriture se confirme ;
     * - avis de paiement : lecture de l'avis, proposition d'import à valider ;
     * - avis sensible (SAFCA) : protocole de prudence ;
     * - autres : confirmation, puis traitement.
     */
    const handleProcess = async (avis: DiscoveredAvis, force: boolean = false) => {
        if (!token) return;

        if (avis.caution_flag && !force) {
            setCautionModalAvis(avis);
            return;
        }
        if (avis.category === 'PAIEMENT_DIVIDENDE') {
            await handleExtraction(avis);
            return;
        }
        if (avis.category !== 'CALENDRIER_DIVIDENDES' && !force) {
            setConfirmation({ type: 'process', avis });
            return;
        }

        setProcessingId(avis.id);
        setProcessErrors(prev => ({ ...prev, [avis.id]: '' }));
        try {
            if (avis.category === 'CALENDRIER_DIVIDENDES') {
                const data = await appelProcess(avis, { force, dry_run: true });
                if (data.details?.bilan) setBilanCalendrier({ avis, bilan: data.details.bilan });
            } else {
                const data = await appelProcess(avis, { force });
                setProcessResults(prev => ({ ...prev, [avis.id]: data }));
                setCautionModalAvis(null);
                fetchAvis();
            }
        } catch (error) {
            setProcessErrors(prev => ({ ...prev, [avis.id]: error instanceof Error ? error.message : 'Erreur réseau.' }));
        } finally {
            setProcessingId(null);
        }
    };

    const handleAppliquerCalendrier = async () => {
        if (!bilanCalendrier) return;
        const { avis } = bilanCalendrier;
        setModalLoading(true);
        try {
            const data = await appelProcess(avis, { dry_run: false });
            setProcessResults(prev => ({ ...prev, [avis.id]: data }));
            if (data.details?.bilan) setBilanCalendrier({ avis, bilan: data.details.bilan });
            const n = data.details?.bilan?.compteurs?.INSERTION || 0;
            showToast(
                data.status === 'TRAITE'
                    ? `Calendrier traité : ${n} dividende(s) enregistré(s).`
                    : `${n} dividende(s) enregistré(s) ; des lignes restent à revoir.`,
                data.status === 'TRAITE' ? 'success' : 'warning',
            );
            fetchAvis();
        } catch (error) {
            showToast(error instanceof Error ? error.message : 'Erreur réseau.', 'error');
        } finally {
            setModalLoading(false);
        }
    };

    const handleExtraction = async (avis: DiscoveredAvis) => {
        setProcessingId(avis.id);
        setProcessErrors(prev => ({ ...prev, [avis.id]: '' }));
        try {
            const response = await fetch(`${API_URL}/v1/admin/discovered-avis/${avis.id}/extract-dividende`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json();
            if (!response.ok) throw new Error(messageErreur(data, "Lecture de l'avis impossible."));
            setExtraction({ avis, data: data.data, meta: data.meta || {} });
            if (data.meta?.deja_en_base) {
                showToast('Dividende déjà en base : avis marqué « Traité ».', 'success');
                fetchAvis();
            }
        } catch (error) {
            setProcessErrors(prev => ({ ...prev, [avis.id]: error instanceof Error ? error.message : 'Erreur réseau.' }));
        } finally {
            setProcessingId(null);
        }
    };

    const handleImporterDividende = async () => {
        if (!extraction) return;
        const { avis, data: x } = extraction;
        if (!x.societe_symbole || x.exercice == null || x.montant_brut_propose == null) return;
        setModalLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/discovered-avis/${avis.id}/import-dividende`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    societe_symbole: x.societe_symbole,
                    exercice: x.exercice,
                    montant_brut: x.montant_brut_propose,
                    date_paiement: x.date_paiement || null,
                    date_detachement: x.date_detachement || null,
                    date_annonce: x.date_annonce || null,
                }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(messageErreur(data, "Import du dividende refusé."));
            const d = data.data;
            showToast(
                d.geste === 'REVISION'
                    ? `${d.societe_symbole} ${d.exercice} révisé : ${fcfa(d.montant_brut_avant)} → ${fcfa(d.montant_brut)} brut.`
                    : `${d.societe_symbole} ${d.exercice} enregistré : ${fcfa(d.montant_brut)} brut, ${fcfa(d.montant_net)} net (Annoncé).`,
                'success',
            );
            setProcessResults(prev => ({ ...prev, [avis.id]: { details: { action: 'IMPORT_DIVIDENDE_AVIS' } } }));
            setExtraction(null);
            fetchAvis();
        } catch (error) {
            showToast(error instanceof Error ? error.message : 'Erreur réseau.', 'error');
        } finally {
            setModalLoading(false);
        }
    };

    const handleDismiss = async (avisId: number) => {
        if (!token) return;
        try {
            const response = await fetch(`${API_URL}/v1/admin/discovered-avis/${avisId}/dismiss`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                showToast('Avis ignoré.', 'info');
                fetchAvis();
            } else {
                showToast("L'avis n'a pas pu être ignoré.", 'error');
            }
        } catch {
            showToast("Erreur réseau : l'avis n'a pas pu être ignoré.", 'error');
        }
    };

    const handleConfirmation = async () => {
        if (!confirmation) return;
        const { type, avis } = confirmation;
        setConfirmation(null);
        if (type === 'dismiss') {
            await handleDismiss(avis.id);
        } else {
            await handleProcess(avis, true);
        }
    };

    return (
        <SiteLayout>
            <Header icon='notifications_active' title='Avis BRVM Détectés' />
            <div className="admin-avis-page">
                {/* Hero Header & Scan Toolbar */}
                <div className="avis-hero-header">
                    <div className="avis-title-group">
                        <h1>
                            <i className="material-icons">radar</i>
                            Avis BRVM Détectés
                        </h1>
                        <p>
                            Surveillance continue, qualification automatique et intégration des avis officiels de la BRVM (Dividendes, Fractionnements, OST).
                        </p>
                    </div>

                    <div className="avis-scan-toolbar">
                        <select
                            value={scanPages}
                            onChange={e => setScanPages(Number(e.target.value))}
                            className="scan-pages-select"
                            disabled={scanLoading}
                        >
                            <option value={3}>3 pages (~30 avis)</option>
                            <option value={5}>5 pages (~50 avis)</option>
                            <option value={10}>10 pages (~100 avis)</option>
                            <option value={20}>20 pages (~200 avis)</option>
                        </select>
                        <button
                            onClick={handleTriggerScan}
                            disabled={scanLoading}
                            className="btn-scan-trigger"
                        >
                            <i className={`material-icons ${scanLoading ? 'spin-anim' : ''}`}>
                                {scanLoading ? 'sync' : 'travel_explore'}
                            </i>
                            {scanLoading ? 'Scan en cours...' : 'Scanner les Avis BRVM'}
                        </button>
                        <button
                            onClick={() => setCompletudeOuverte(true)}
                            className="btn-scan-trigger btn-completude"
                            title="Pour chaque société cotée : dividende en base, aucun, avis non importé, ou rien"
                        >
                            <i className="material-icons">fact_check</i>
                            Complétude des dividendes
                        </button>
                    </div>
                </div>

                {/* Scan Notification Alert */}
                {scanMessage && (
                    <div className={`scan-alert-banner ${scanMessage.isError ? 'error' : ''}`}>
                        <div className="scan-alert-left">
                            <i className="material-icons">
                                {scanMessage.isError ? 'error_outline' : 'check_circle'}
                            </i>
                            <span>{scanMessage.text}</span>
                        </div>
                        <button onClick={() => setScanMessage(null)} className="btn-close-alert">
                            <i className="material-icons">close</i>
                        </button>
                    </div>
                )}

                {/* KPI Summary Cards */}
                <div className="avis-kpi-grid">
                    <div
                        className={`avis-kpi-card ${!statutFilter && !cautionOnly ? 'active' : ''}`}
                        onClick={handleResetFilters}
                    >
                        <div className="kpi-icon-wrap blue">
                            <i className="material-icons">list_alt</i>
                        </div>
                        <div className="kpi-info">
                            <span className="kpi-label">Total Détectés</span>
                            <span className="kpi-value">{kpis.total}</span>
                        </div>
                    </div>

                    <div
                        className={`avis-kpi-card ${statutFilter === 'NOUVEAU' ? 'active' : ''}`}
                        onClick={() => { setStatutFilter('NOUVEAU'); setCautionOnly(false); }}
                    >
                        <div className="kpi-icon-wrap blue">
                            <i className="material-icons">fiber_new</i>
                        </div>
                        <div className="kpi-info">
                            <span className="kpi-label">Nouveaux à Traiter</span>
                            <span className="kpi-value">{kpis.nouveaux}</span>
                        </div>
                    </div>

                    <div
                        className={`avis-kpi-card ${statutFilter === 'TRAITE' ? 'active' : ''}`}
                        onClick={() => { setStatutFilter('TRAITE'); setCautionOnly(false); }}
                    >
                        <div className="kpi-icon-wrap emerald">
                            <i className="material-icons">check_circle_outline</i>
                        </div>
                        <div className="kpi-info">
                            <span className="kpi-label">Traités avec Succès</span>
                            <span className="kpi-value">{kpis.traites}</span>
                        </div>
                    </div>

                    <div
                        className={`avis-kpi-card ${cautionOnly ? 'active' : ''}`}
                        onClick={() => { setCautionOnly(true); setStatutFilter(''); }}
                    >
                        <div className="kpi-icon-wrap amber">
                            <i className="material-icons">warning_amber</i>
                        </div>
                        <div className="kpi-info">
                            <span className="kpi-label">Revue Requise (SAFCA)</span>
                            <span className="kpi-value">{kpis.cautions}</span>
                        </div>
                    </div>

                    <div
                        className={`avis-kpi-card ${statutFilter === 'IGNORE' ? 'active' : ''}`}
                        onClick={() => { setStatutFilter('IGNORE'); setCautionOnly(false); }}
                    >
                        <div className="kpi-icon-wrap slate">
                            <i className="material-icons">visibility_off</i>
                        </div>
                        <div className="kpi-info">
                            <span className="kpi-label">Avis Ignorés</span>
                            <span className="kpi-value">{kpis.ignores}</span>
                        </div>
                    </div>
                </div>

                {/* Filters Panel */}
                <div className="avis-filters-card">
                    <div className="filters-card-header">
                        <div className="filters-card-title">
                            <i className="material-icons">filter_list</i>
                            Filtres de recherche
                        </div>
                        {hasActiveFilters && (
                            <button onClick={handleResetFilters} className="btn-reset-filters">
                                <i className="material-icons" style={{ fontSize: '14px' }}>refresh</i>
                                Réinitialiser les filtres
                            </button>
                        )}
                    </div>

                    <div className="filters-grid">
                        <div className="filter-item">
                            <label>Statut</label>
                            <select
                                value={statutFilter}
                                onChange={e => setStatutFilter(e.target.value)}
                                className="filter-control"
                            >
                                <option value="">Tous les statuts</option>
                                <option value="NOUVEAU">Nouveau</option>
                                <option value="EN_ATTENTE_REVUE">En attente revue</option>
                                <option value="TRAITE">Traité</option>
                                <option value="IGNORE">Ignoré</option>
                                <option value="ERREUR">Erreur</option>
                            </select>
                        </div>

                        <div className="filter-item">
                            <label>Catégorie</label>
                            <select
                                value={categoryFilter}
                                onChange={e => setCategoryFilter(e.target.value)}
                                className="filter-control"
                            >
                                <option value="">Toutes les catégories</option>
                                <option value="CALENDRIER_DIVIDENDES">Calendrier Dividendes</option>
                                <option value="FRACTIONNEMENT">Fractionnement</option>
                                <option value="AUGMENTATION_CAPITAL">Augmentation Capital</option>
                                <option value="PAIEMENT_DIVIDENDE">Paiement Dividende</option>
                                <option value="COMMUNIQUE">Communiqué</option>
                                <option value="AUTRE">Autre</option>
                            </select>
                        </div>

                        <div className="filter-item">
                            <label>Société</label>
                            <select
                                value={societeFilter}
                                onChange={e => setSocieteFilter(e.target.value)}
                                className="filter-control"
                            >
                                <option value="">Toutes les sociétés</option>
                                {stocks.map(s => (
                                    <option key={s.symbole} value={s.symbole}>{s.symbole} - {s.nom_societe}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-item">
                            <label>Dividende</label>
                            <select
                                value={enBaseFilter}
                                onChange={e => setEnBaseFilter(e.target.value)}
                                className="filter-control"
                                title="Avis de paiement de dividendes uniquement"
                            >
                                <option value="">Tous</option>
                                <option value="false">Non importés</option>
                                <option value="true">Déjà en base</option>
                            </select>
                        </div>

                        <div className="filter-item">
                            <label>Date Début</label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={e => setDateFrom(e.target.value)}
                                className="filter-control"
                            />
                        </div>

                        <div className="filter-item">
                            <label>Date Fin</label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={e => setDateTo(e.target.value)}
                                className="filter-control"
                            />
                        </div>

                        <div className="filter-item">
                            <label style={{ visibility: 'hidden' }}>Option</label>
                            <label className={`filter-caution-pill ${cautionOnly ? 'active' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={cautionOnly}
                                    onChange={e => setCautionOnly(e.target.checked)}
                                />
                                <span className="filter-caution-label">
                                    <i className="material-icons" style={{ fontSize: '15px' }}>warning</i>
                                    Prudence (SAFCA)
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="avis-table-card">
                    <div className="table-header-strip">
                        <div className="table-header-title">
                            <span>Liste des avis détectés</span>
                            <span className="table-count-badge">{avisList.length} avis</span>
                        </div>
                    </div>

                    {loading ? (
                        <div className="table-empty-state">
                            <i className="material-icons spin-anim">sync</i>
                            <h3>Chargement des avis BRVM...</h3>
                            <p>Veuillez patienter pendant la récupération des données.</p>
                        </div>
                    ) : avisList.length === 0 ? (
                        <div className="table-empty-state">
                            <i className="material-icons">find_in_page</i>
                            <h3>Aucun avis trouvé</h3>
                            <p>Aucun avis officiel ne correspond à vos critères de recherche actuels.</p>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="avis-data-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '110px' }}>Date</th>
                                        <th style={{ width: '180px' }}>Catégorie</th>
                                        <th style={{ width: '110px' }}>Société</th>
                                        <th>Titre de l'avis & Document</th>
                                        <th style={{ width: '130px' }}>Statut</th>
                                        <th style={{ width: '220px', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {avisList.map(avis => {
                                        const isEditing = editingId === avis.id;
                                        const isProcessing = processingId === avis.id;
                                        const error = processErrors[avis.id];
                                        const res = processResults[avis.id];

                                        return (
                                            <React.Fragment key={avis.id}>
                                                <tr className={`avis-row ${avis.caution_flag ? 'caution-row' : ''}`}>
                                                    <td>
                                                        <div className="date-cell">
                                                            <i className="material-icons">calendar_today</i>
                                                            {avis.date_publication || '-'}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        {isEditing ? (
                                                            <select
                                                                value={editingCategory}
                                                                onChange={e => setEditingCategory(e.target.value)}
                                                                className="filter-control"
                                                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                                            >
                                                                <option value="CALENDRIER_DIVIDENDES">Calendrier Dividendes</option>
                                                                <option value="FRACTIONNEMENT">Fractionnement</option>
                                                                <option value="AUGMENTATION_CAPITAL">Augmentation Capital</option>
                                                                <option value="PAIEMENT_DIVIDENDE">Paiement Dividende</option>
                                                                <option value="COMMUNIQUE">Communiqué</option>
                                                                <option value="AUTRE">Autre</option>
                                                            </select>
                                                        ) : (
                                                            <CategoryBadge category={avis.category} />
                                                        )}
                                                    </td>

                                                    <td>
                                                        {isEditing ? (
                                                            <select
                                                                value={editingSymbole}
                                                                onChange={e => setEditingSymbole(e.target.value)}
                                                                className="filter-control"
                                                                style={{ padding: '4px 8px', fontSize: '12px' }}
                                                            >
                                                                <option value="">(Non identifié)</option>
                                                                {stocks.map(s => (
                                                                    <option key={s.symbole} value={s.symbole}>{s.symbole} - {s.nom_societe}</option>
                                                                ))}
                                                            </select>
                                                        ) : avis.societe_symbole ? (
                                                            <span className="stock-pill">
                                                                {avis.societe_symbole}
                                                            </span>
                                                        ) : (
                                                            <span className="stock-general">Marché</span>
                                                        )}
                                                    </td>

                                                    <td className="notice-title-col">
                                                        <div className="notice-title-text">{avis.title}</div>
                                                        <div className="notice-meta-links">
                                                            <a
                                                                href={avis.avis_url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="pdf-link-btn"
                                                            >
                                                                <i className="material-icons">picture_as_pdf</i>
                                                                Voir le PDF officiel
                                                            </a>

                                                            {avis.category === 'PAIEMENT_DIVIDENDE' && avis.dividende_en_base != null && (
                                                                <span className={`issue-badge ${avis.dividende_en_base ? 'issue-ok' : 'issue-new'}`}>
                                                                    {avis.dividende_en_base
                                                                        ? `Dividende ${avis.exercice} en base`
                                                                        : `Dividende ${avis.exercice} non importé`}
                                                                </span>
                                                            )}

                                                            {avis.caution_flag && (
                                                                <span className="caution-tag">
                                                                    <i className="material-icons">warning</i>
                                                                    PRUDENCE : FACTEUR TERP (SAFCA)
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <StatutBadge statut={avis.statut} />
                                                    </td>

                                                    <td className="actions-cell">
                                                        {isEditing ? (
                                                            <div className="actions-group">
                                                                <button
                                                                    onClick={() => handleSaveEdit(avis.id)}
                                                                    className="btn-table-action btn-action-save"
                                                                >
                                                                    <i className="material-icons">check</i>
                                                                    Enregistrer
                                                                </button>
                                                                <button
                                                                    onClick={() => setEditingId(null)}
                                                                    className="btn-table-action btn-action-secondary"
                                                                >
                                                                    Annuler
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="actions-group">
                                                                {(avis.statut !== 'TRAITE' || avis.category === 'CALENDRIER_DIVIDENDES') && (
                                                                    <button
                                                                        onClick={() => handleProcess(avis)}
                                                                        disabled={isProcessing}
                                                                        className={`btn-table-action ${avis.caution_flag ? 'btn-action-caution' : 'btn-action-process'}`}
                                                                        title={avis.category === 'CALENDRIER_DIVIDENDES'
                                                                            ? 'Simule le calendrier : les dividendes déjà en base sont reconnus, rien n’est écrit avant confirmation'
                                                                            : avis.category === 'PAIEMENT_DIVIDENDE'
                                                                                ? 'Lit l’avis et propose le dividende à enregistrer'
                                                                                : undefined}
                                                                    >
                                                                        <i className={`material-icons ${isProcessing ? 'spin-anim' : ''}`}>
                                                                            {isProcessing ? 'sync' : (avis.caution_flag ? 'warning' : 'bolt')}
                                                                        </i>
                                                                        {isProcessing ? 'Traitement...'
                                                                            : avis.caution_flag ? 'Examiner'
                                                                                : avis.statut === 'TRAITE' ? 'Revérifier' : 'Traiter'}
                                                                    </button>
                                                                )}

                                                                <button
                                                                    onClick={() => handleStartEdit(avis)}
                                                                    className="btn-table-action btn-action-secondary"
                                                                    title="Corriger la société ou la catégorie"
                                                                >
                                                                    <i className="material-icons">edit</i>
                                                                    Corriger
                                                                </button>

                                                                {avis.statut !== 'IGNORE' && (
                                                                    <button
                                                                        onClick={() => setConfirmation({ type: 'dismiss', avis })}
                                                                        className="btn-table-action btn-action-dismiss"
                                                                        title="Ignorer cet avis"
                                                                    >
                                                                        <i className="material-icons">close</i>
                                                                        Ignorer
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>

                                                {/* Error Feedback */}
                                                {error && (
                                                    <tr className="result-feedback-row error">
                                                        <td colSpan={6}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <i className="material-icons" style={{ fontSize: '16px' }}>error</i>
                                                                <span>{error}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                                {/* Success Feedback */}
                                                {res && (
                                                    <tr className="result-feedback-row success">
                                                        <td colSpan={6}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <i className="material-icons" style={{ fontSize: '16px' }}>check_circle</i>
                                                                <span>{resumeTraitement(res)}</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Caution Modal for SAFCA & Sensitive Corporate Actions */}
                {cautionModalAvis && (
                    <div className="modal-backdrop-blur" onClick={() => setCautionModalAvis(null)}>
                        <div className="caution-modal-box" onClick={e => e.stopPropagation()}>
                            <div className="caution-modal-header">
                                <i className="material-icons">warning</i>
                                <h3>Protocole de Prudence : Augmentation de Capital Numéraire (SAFCA)</h3>
                            </div>

                            <p style={{ fontSize: '14px', color: 'var(--text-main)', lineHeight: '1.5', margin: '0 0 12px' }}>
                                <strong>Avis sélectionné :</strong> {cautionModalAvis.title}
                            </p>

                            <div className="caution-callout-box">
                                <strong>Règle d'ajustement financier institutionnel :</strong>
                                {cautionModalAvis.caution_reason || "Cette augmentation de capital s'est faite avec apport de numéraire. Le facteur titres (x2.0) ne doit pas être appliqué directement aux dividendes ou bénéfices historiques sous peine d'écraser artificiellement les performances passées. Le système applique le facteur cours de référence TERP (1.4152)."}
                            </div>

                            <div className="modal-actions-bar">
                                <button
                                    onClick={() => setCautionModalAvis(null)}
                                    className="btn-table-action btn-action-secondary"
                                    style={{ padding: '8px 16px', fontSize: '13px' }}
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={() => handleProcess(cautionModalAvis, true)}
                                    className="btn-table-action btn-action-caution"
                                    style={{ padding: '8px 18px', fontSize: '13px' }}
                                >
                                    <i className="material-icons">verified</i>
                                    Confirmer avec Facteur Cours TERP (1.4152)
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {bilanCalendrier && (
                    <BilanCalendrierModal
                        titre={`Calendrier publié le ${bilanCalendrier.avis.date_publication || '?'}`}
                        bilan={bilanCalendrier.bilan}
                        isLoading={modalLoading}
                        onAppliquer={handleAppliquerCalendrier}
                        onFermer={() => setBilanCalendrier(null)}
                    />
                )}

                {extraction && (
                    <ExtractionAvisModal
                        titre={extraction.avis.title}
                        extraction={extraction.data}
                        meta={extraction.meta}
                        isLoading={modalLoading}
                        onImporter={handleImporterDividende}
                        onFermer={() => setExtraction(null)}
                    />
                )}

                {completudeOuverte && (
                    <CompletudeModal
                        completude={completude}
                        exercice={completudeExercice}
                        isLoading={completudeLoading}
                        onExercice={setCompletudeExercice}
                        onFermer={() => setCompletudeOuverte(false)}
                    />
                )}

                <ConfirmModal
                    isOpen={confirmation !== null}
                    title={confirmation?.type === 'dismiss' ? 'Ignorer cet avis' : 'Traiter cet avis'}
                    message={
                        confirmation?.type === 'dismiss'
                            ? `« ${confirmation.avis.title} » passera en « Ignoré » et disparaîtra des avis à traiter. Aucune donnée n'est modifiée.`
                            : confirmation?.avis.category === 'FRACTIONNEMENT' || confirmation?.avis.category === 'AUGMENTATION_CAPITAL'
                                ? `« ${confirmation?.avis.title} » : l'opération sur titres sera enregistrée dans les opérations (corporate_actions), qui ajustent l'historique des cours et des dividendes.`
                                : `« ${confirmation?.avis.title} » sera marqué « Traité ». Aucune donnée de marché n'est modifiée pour cette catégorie.`
                    }
                    confirmLabel={confirmation?.type === 'dismiss' ? 'Ignorer' : 'Traiter'}
                    isDestructive={confirmation?.type === 'dismiss'}
                    onConfirm={handleConfirmation}
                    onCancel={() => setConfirmation(null)}
                />
            </div>
        </SiteLayout>
    );
};

export default AdminDiscoveredAvisScreen;
